import type { SimpleMetric } from './model';
import Badge, { type BadgeColor } from '@/components/common/Badge';
import { getModuleIcon } from '@/utils/moduleIcons';

interface CognitiveFitnessProps {
    name: string;
    description: string;
    moduleDefinitionIdentifier?: string;
    result: SimpleMetric[];

    note?: {
        label?: string;
        description: string;
    };
}

const CognitiveFitness = ({ name, description, moduleDefinitionIdentifier, result, note }: CognitiveFitnessProps) => {
    const IconComp = moduleDefinitionIdentifier ? getModuleIcon(moduleDefinitionIdentifier) : null;

    return (
        <div className="rounded-lg bg-teal-100 p-5">
            <div className="flex flex-col gap-5">
                <div className="pdf-avoid-break flex flex-1 flex-col gap-5">
                    <div className="flex items-center gap-2.5">
                        {IconComp && (
                            <IconComp className="h-5 w-5 shrink-0 text-teal-600" strokeWidth={2} aria-hidden="true" />
                        )}
                        <h4 className="text-base leading-6 font-semibold text-black">{name}</h4>
                    </div>
                    <p className="text-base leading-6 font-medium text-neutral-500">{description}</p>
                </div>
                <div className="pdf-avoid-break flex flex-1 gap-5">
                    {result.map((metric, idx) => (
                        <div
                            key={idx}
                            className="bg-neutral-0 flex flex-1 flex-col items-center justify-center gap-5 rounded-lg p-5"
                        >
                            <div className="flex flex-col items-center gap-2.5">
                                <div className="text-center text-xs leading-4.5 font-medium text-neutral-500">
                                    {metric.label}
                                </div>
                                <div className="flex items-center justify-center gap-1.5">
                                    <span className="text-center text-xl leading-7 font-bold text-neutral-900">
                                        {metric.value}
                                    </span>
                                    {metric.badgeLabel && (
                                        <Badge
                                            text={metric.badgeLabel}
                                            color={(metric.badgeColor as BadgeColor) ?? 'gray'}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {note && (
                    <div className="pdf-avoid-break flex flex-1 flex-row gap-1">
                        {note.label && <h4 className="text-base leading-6 font-semibold text-black">{note.label}:</h4>}
                        <p className="text-base leading-6 font-medium text-neutral-500">{note.description}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CognitiveFitness;
